import { useEffect, useMemo } from 'react'
import {
  Vector3,
  MeshBasicMaterial,
  ColorRepresentation,
  LineCurve3,
  ConeGeometry,
  Quaternion,
} from 'three'
import { useTheme } from '@mui/material'
import Text3DThreeDoubleSided from '../Text3DThreeDoubleSided'

interface Props {
  direction: Vector3
  origin: Vector3
  length: number
  label?: string | string[]
  labelPosition?: Vector3
  labelAnchorLocation?: 'top' | 'bottom' | 'left' | 'right' | 'center'
  labelScale?: number
  color?: string
  backgroundColor?: string
  highlighted?: boolean
  lineWidth?: number
  headLength?: number
  headWidth?: number
  renderOnTop?: boolean
  opacity?: number
  textXDirection?: Vector3
  textYDirection?: Vector3
  textOffsetHalfWidth?: number
  onClick?: () => void
}

const RENDER_ON_TOP_ORDER = 999

const Arrow = ({
  direction,
  origin,
  length,
  label,
  labelPosition,

  color,
  backgroundColor,
  onClick = () => null,
  highlighted,
  labelAnchorLocation = 'top',
  labelScale = 0.1,
  lineWidth = 0.01,
  headLength = 0.15,
  headWidth = 0.1,
  renderOnTop = false,
  opacity = 1,
  textXDirection = new Vector3(1, 0, 0),
  textYDirection = new Vector3(0, 0, 1),
  textOffsetHalfWidth = 0.005,
}: Props) => {
  const theme = useTheme()
  const arrowColor = backgroundColor || theme.palette.common.black

  const normalizedDirection = useMemo(() => direction.clone().normalize(), [direction])

  const tip = useMemo(
    () => origin.clone().add(normalizedDirection.clone().multiplyScalar(length)),
    [origin, normalizedDirection, length],
  )

  const shaftEnd = useMemo(
    () => tip.clone().sub(normalizedDirection.clone().multiplyScalar(headLength)),
    [tip, normalizedDirection, headLength],
  )

  const shaftCurve = useMemo(
    () => (length > headLength ? new LineCurve3(origin, shaftEnd) : null),
    [origin, shaftEnd, length, headLength],
  )

  const headQuaternion = useMemo(() => {
    const quaternion = new Quaternion()
    quaternion.setFromUnitVectors(new Vector3(0, 1, 0), normalizedDirection)
    return quaternion
  }, [normalizedDirection])

  const headPosition = useMemo(
    () => tip.clone().sub(normalizedDirection.clone().multiplyScalar(headLength / 2)),
    [tip, normalizedDirection, headLength],
  )

  const shaftMaterial = useMemo(
    () =>
      new MeshBasicMaterial({
        color: arrowColor as ColorRepresentation,
        depthTest: !renderOnTop,
        depthWrite: !renderOnTop && opacity >= 1,
        transparent: renderOnTop || opacity < 1,
        opacity,
        toneMapped: false,
      }),
    [arrowColor, renderOnTop, opacity],
  )

  const headMaterial = useMemo(
    () =>
      new MeshBasicMaterial({
        color: arrowColor as ColorRepresentation,
        depthTest: !renderOnTop,
        depthWrite: !renderOnTop && opacity >= 1,
        transparent: renderOnTop || opacity < 1,
        opacity,
        toneMapped: false,
      }),
    [arrowColor, renderOnTop, opacity],
  )

  const headGeometry = useMemo(
    () => new ConeGeometry(headWidth / 2, headLength, 12),
    [headWidth, headLength],
  )

  const shaftRadius = lineWidth / 2
  const renderOrder = renderOnTop ? RENDER_ON_TOP_ORDER : undefined
  const textLines = useMemo(() => {
    if (!label) return []

    return Array.isArray(label) ? label : [label]
  }, [label])

  useEffect(() => {
    return () => {
      shaftMaterial.dispose()
      headMaterial.dispose()
      headGeometry.dispose()
    }
  }, [shaftMaterial, headMaterial, headGeometry])

  return (
    <group key={length} renderOrder={renderOrder}>
      {shaftCurve && (
        <mesh material={shaftMaterial} renderOrder={renderOrder} onClick={onClick}>
          <tubeGeometry args={[shaftCurve, 4, shaftRadius, 8, false]} />
        </mesh>
      )}
      <mesh
        geometry={headGeometry}
        material={headMaterial}
        position={headPosition}
        quaternion={headQuaternion}
        renderOrder={renderOrder}
        onClick={onClick}
      />
      {textLines.length > 0 && (
        <Text3DThreeDoubleSided
          textLines={textLines}
          color={color || (highlighted ? theme.palette.text.primary : theme.palette.grey[100])}
          backgroundColor={
            backgroundColor ||
            (highlighted ? (theme.scenePalette.highlight as string) : theme.palette.primary.main)
          }
          borderWidth={0.005}
          borderColor={
            color || (highlighted ? theme.palette.text.primary : theme.palette.grey[100])
          }
          position={labelPosition ?? origin}
          offsetBetweenTxtElementsHalfWidth={textOffsetHalfWidth}
          anchorLocation={labelAnchorLocation}
          scale={labelScale}
          opacity={opacity}
          onClick={onClick ? () => onClick() : undefined}
          textXOrientation={textXDirection}
          textYOrientation={textYDirection}
        />
      )}
    </group>
  )
}

export default Arrow
